import { motion } from 'framer-motion'
import { type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import Icon from '@/components/Icon'

import { ModalBackdrop, ModalBody, ModalClose, ModalDialog, ModalFooter, ModalIcon, type ModalVariant, ModalWrapper } from './Modal.styles'

interface ModalProps {
  children: ReactNode
  onClose?: () => void
  icon?: string
  variant?: ModalVariant
  showClose?: boolean
  footer?: ReactNode
}

const Modal = ({ children, onClose, icon, variant = 'default', showClose = true, footer }: ModalProps) => {
  const dialogAnimationProps =
    variant === 'full'
      ? {
          initial: { y: 50, opacity: 0 },
          animate: { y: 0, opacity: 1 },
          exit: { y: 50, opacity: 0 },
        }
      : {
          initial: { scale: 0.95, opacity: 0 },
          animate: { scale: 1, opacity: 1 },
          exit: { scale: 1.05, opacity: 0 },
        }

  const handleClose = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    if (!onClose) {
      return
    }

    onClose()
  }

  const modalRoot = document.querySelector('#modal')
  if (!modalRoot) {
    return null
  }

  return createPortal(
    <ModalWrapper
      as={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
      $variant={variant}
    >
      <ModalBackdrop onClick={handleClose}></ModalBackdrop>
      <ModalDialog as={motion.div} {...dialogAnimationProps}>
        {showClose && (
          <ModalClose onClick={handleClose}>
            <Icon name="close"></Icon>
          </ModalClose>
        )}
        <ModalBody>
          {icon && (
            <ModalIcon>
              <Icon name={icon}></Icon>
            </ModalIcon>
          )}
          {children}
        </ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalDialog>
    </ModalWrapper>,
    modalRoot
  )
}

Modal.displayName = 'Modal'

export default Modal
